import { getAbsolutePoints, transformPointsToOriginalScale } from 'components/DisplayEnergy/util';
import Konva from 'konva';
import { normalizeAngle } from '../utils';
import { getRoofSegmentIDFromGroupID, KonvaGroupNames } from '../utils';
import { DESIGN_MODES } from '../../../constants';
import { resolveFacetPitchDegrees } from 'components/FacetInputs/util';
import { NewlyCreatedFacet } from 'store/slices/ToolSlice/types';
import { isModeDefault, isInHouseSimulationMode } from '../../../util';

type TranslatePosPayload = {
	segmentId: string,
	azimuth: number,
	previousAzimuthDegrees: number,
	translatedPos: {
		[id: string]: {
			pos: number[][]
		}
	};
	pitch?: number;
	shading?: number;
	previousShading?: number;
	previousPitchDegrees?: number;

}

function getAbsolutePointsOfTranslatedPanels(stage: Konva.Stage, 
	roofIndexes: string[],
	allRoofSegs: RasterRoofSegment[], 
	currentScale: Vector2d
) {
	try {
		const panelGroups = stage.find(`.${KonvaGroupNames.existingPanelsGroup}`)
			.filter(g => roofIndexes.includes(getRoofSegmentIDFromGroupID(g.attrs.id))) as Konva.Group[];

		const newPos: TranslatePosPayload[] = [];
		// const stagePosition = stage.position();

		panelGroups?.forEach((g) => {
			const translatedPos: typeof newPos[0]['translatedPos'] = {};
			const segmentId = getRoofSegmentIDFromGroupID(g.attrs.id);

			g.children?.forEach((child) => {
				if (child.className === 'Line') {
					const newPoints = (getAbsolutePoints(child as Konva.Line, currentScale, stage) as number[]);
					//translated postion for panels 
					translatedPos[child.attrs.id] = {
						pos: [...transformFlatArrayToCoordinateTypeArray(newPoints)]

					};
				}
			});
			const roofSegment = allRoofSegs.find(rs => rs.id === segmentId);
			// todo push like previously or continue like this
			const newPitch = g.getAttr('pitch');
			const shading = g.getAttr('shading');
			if (!roofSegment) return [];
			newPos.push({
				segmentId,
				azimuth: normalizeAngle(g.rotation() - roofSegment.azimuthDegrees),
				previousAzimuthDegrees: roofSegment.azimuthDegrees,
				translatedPos,
				previousPitchDegrees: roofSegment.pitchDegrees,
				pitch: newPitch ?? roofSegment.pitchDegrees,
				shading: shading ?? roofSegment.shading,
				previousShading: roofSegment.shading,
			});

		});
		console.log(newPos);
		return newPos;
	} catch (e) {
		console.log('errr', e);
		throw new Error('E_WHILE_GETTING_TRANSLATION_FOR_SEGMENT');
	}
}

function transformFlatArrayToCoordinateTypeArray(list: number[]): number[][] {
	const transformedArray = [];
	for (let i = 0; i <= list.length - 2; i += 2) {
		transformedArray.push([list[i], list[i + 1]]);
	}
	return transformedArray;
}

function transformNewlyAddedFacets(params: { 
	mode: DesignModes, 
	newlyCreatedFacets: Record<string, NewlyCreatedFacet>, 
	scale: Vector2d, 
	designType: DesignType,
	defaultShading: number,
	defaultFlatPitchDeg: number,
}) {

	const { mode, newlyCreatedFacets, scale, designType, defaultShading, defaultFlatPitchDeg } = params;
	if (!Object.keys(newlyCreatedFacets).length) {
		return [];
	}

	const isSolarMode = designType == 'SOLAR';
	if (isModeDefault(mode)) {
		return Object.values(newlyCreatedFacets).map(facet => {

			const newlyCreatedFacet: Omit<NewlyCreatedFacet, 'konvaPolygonId'> = {
				hullCoords: facet.hullCoords.map(coord => (transformPointsToOriginalScale(scale, coord))),
				azimuthDegrees: facet.azimuthDegrees,
				pitchDegrees: resolveFacetPitchDegrees(facet.pitchDegrees, {
					isGroundMount: facet?.isGroundMount,
					mode,
					isSolarDesign: isSolarMode,
					defaultFlatPitchDeg,
				}),
				isGroundMount: facet.isGroundMount,
				noGapGroundMount: facet.noGapGroundMount,
			};
			
			if (newlyCreatedFacet.isGroundMount && isSolarMode) {
				if(!newlyCreatedFacet.noGapGroundMount) newlyCreatedFacet.rowSpacing = facet.rowSpacing;
				newlyCreatedFacet.shading = facet.shading != null ? +facet.shading : defaultShading;
			}

			if(isInHouseSimulationMode(mode)){
				newlyCreatedFacet.shading = undefined;
				if(newlyCreatedFacet.isGroundMount) newlyCreatedFacet.startingHeight = facet.startingHeight;
			}

			return newlyCreatedFacet;
		});
	}

	return Object.values(newlyCreatedFacets).map(facet => ({
		hullCoords: facet.hullCoords.map(coord => (transformPointsToOriginalScale(scale, coord))),
		azimuthDegrees: facet.azimuthDegrees,
		pitchDegrees: resolveFacetPitchDegrees(facet.pitchDegrees, {
			isGroundMount: facet?.isGroundMount,
			mode,
			isSolarDesign: isSolarMode,
			defaultFlatPitchDeg,
		}),
		shading: facet.shading != null ? +facet.shading : defaultShading,
		isGroundMount: facet?.isGroundMount,
		noGapGroundMount: facet?.noGapGroundMount,
	}));
}

function checkIfLayoutNeedsToBeReset(stage: Konva.Stage, roofIndexes: string[], allRoofSegs: RasterRoofSegment[]) {

	const panelGroups = stage?.find(`.${KonvaGroupNames.existingPanelsGroup}`).
		filter(g => roofIndexes.includes(getRoofSegmentIDFromGroupID(g.attrs.id))) as Konva.Group[];

	for (const g of panelGroups || []) {
		const segmentId = getRoofSegmentIDFromGroupID(g.attrs.id);
		const roofSegment = allRoofSegs.find((rs) => rs.id === segmentId);
		const newPitch = g.getAttr('pitch');

		// reset layout if pitch has chnaged;
		if ((newPitch || newPitch === 0) && newPitch !== roofSegment?.pitchDegrees) {
			return true;
		}
	}

	return false;
}

// Mirrors the API's translatePanelsToNewPostion needsShadingRefresh rule.
// A plain move doesn't change a normal facet's flux; only a pitch or azimuth change owes a shading recompute.
// A row ground mount (isGroundMount, not noGap) shades itself row-to-row, so any transform of it, a move included, owes one.
function hasShadingAffectingTransform(stage: Konva.Stage, roofIndexes: string[], allRoofSegs: RasterRoofSegment[]) {

	const hasTransformedRowGroundMount = roofIndexes.some(id => {
		const roofSegment = allRoofSegs.find((rs) => rs.id === id);
		return !!roofSegment?.isGroundMount && !roofSegment.noGapGroundMount;
	});
	if (hasTransformedRowGroundMount) return true;

	const panelGroups = stage?.find(`.${KonvaGroupNames.existingPanelsGroup}`).
		filter(g => roofIndexes.includes(getRoofSegmentIDFromGroupID(g.attrs.id))) as Konva.Group[];

	return (panelGroups || []).some(g => {
		const roofSegment = allRoofSegs.find((rs) => rs.id === getRoofSegmentIDFromGroupID(g.attrs.id));
		if (!roofSegment) return false;

		const newPitch = g.getAttr('pitch');
		const pitchChanged = (newPitch || newPitch === 0) && newPitch !== roofSegment.pitchDegrees;

		const azimuthDelta = g.attrs.rotation != null ? normalizeAngle(g.attrs.rotation - roofSegment.azimuthDegrees) : 0;
		const azimuthChanged = Math.min(azimuthDelta, 360 - azimuthDelta) > 0.01;

		// any mount's rack is baked into the DSM, so moving its row spacing / starting height moves the rack
		const rowSpacing = g.getAttr('rowSpacing');
		const startingHeight = g.getAttr('startingHeight');
		const rackChanged = !!roofSegment.isGroundMount && (
			(rowSpacing != null && !isNaN(Number(rowSpacing)) && rowSpacing !== roofSegment.rowSpacing)
			|| (startingHeight != null && !isNaN(Number(startingHeight)) && startingHeight !== roofSegment.startingHeight));

		return pitchChanged || azimuthChanged || rackChanged;
	});
}

const downloadDataAsJSONFile =(data: object, fileName: string) => {
	const jsonData = JSON.stringify(data, null, 2);
	const blob = new Blob([jsonData], { type: 'application/json' });
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = `${fileName}.json`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export { transformNewlyAddedFacets, 
	getAbsolutePointsOfTranslatedPanels, 
	transformFlatArrayToCoordinateTypeArray ,
	checkIfLayoutNeedsToBeReset,
	hasShadingAffectingTransform,
	downloadDataAsJSONFile
};
